import React, { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { SrIcon } from '@/components/ui/SrIcon';
import { useResetEmployeePassword } from '@/hooks/use-reset-employee-password';
import logoFull from '@/assets/logos/solvrev-logo-full.png';

export default function LoginPage() {
  const { login } = useAuth();
  const resetPassword = useResetEmployeePassword();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const err = await login(email, password);
    if (err) setError('Ugyldig e-post eller passord');
    setLoading(false);
  };

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Skriv inn eposten din først, så sender vi en lenke dit.');
      return;
    }
    resetPassword.mutate(email.trim(), {
      onSuccess: () => {
        toast.success(`Sendt lenke for å nullstille passord til ${email.trim()}.`);
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : String(err);
        toast.error(`Kunne ikke sende reset-lenke: ${msg}`);
      },
    });
  };

  return (
    <div className="min-h-screen w-full grid md:grid-cols-2 bg-[var(--sr-revegraa)]">
        {/* Left: brand hero */}
        <div className="relative overflow-hidden bg-[var(--sr-moerk)] text-[var(--sr-revegraa)] p-10 md:p-12 lg:p-16 flex flex-col justify-between min-h-[420px] md:min-h-screen">
          {/* Background blobs */}
          <div
            className="absolute pointer-events-none"
            style={{ top: -60, right: -60, width: 280, height: 280, color: 'var(--sr-reveblaa)', opacity: 0.55 }}
          >
            <SrIcon name="empty-blob" size="100%" className="w-full h-full" />
          </div>
          <div
            className="absolute pointer-events-none"
            style={{ bottom: -40, right: 60, width: 160, height: 160, color: 'var(--sr-reverosa)', opacity: 0.4 }}
          >
            <SrIcon name="breathing-blob" size="100%" className="w-full h-full" />
          </div>

          {/* Brand */}
          <div className="relative z-10 flex items-center">
            <img src={logoFull} alt="Sølvrev" className="h-10 w-auto" />
          </div>

          {/* Pitch */}
          <div className="relative z-10">
            <div className="text-[11.5px] font-medium uppercase tracking-[0.12em] opacity-55 mb-4">Internsystem</div>
            <h2 className="font-display font-semibold text-white text-[48px] leading-[1.04] tracking-tight">
              Velkommen til Hiet —<br />
              <em className="not-italic text-[var(--sr-reverosa)]">revenes paradis!</em>
            </h2>
          </div>

          <div className="relative z-10 text-[11.5px] font-medium uppercase tracking-[0.08em] opacity-50">
            v2.4 · Bygget av Vibbing
          </div>
        </div>

        {/* Right: form */}
        <div className="bg-white p-8 md:p-14 flex flex-col justify-center md:border-l-[1.5px] md:border-[var(--sr-revemoerk)]">
          <div className="w-full max-w-md mx-auto">
            <h3 className="font-display font-semibold text-[28px] tracking-tight text-[var(--sr-revemoerk)] mb-1.5">Logg inn</h3>
            <p className="opacity-75 text-[14px] mb-7">Bruk Sølvrev-eposten din. Første gang? Be Sjefen om tilgang.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-[12px] font-medium uppercase tracking-[0.04em] opacity-70">
                Epost
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="navn@solvrev.no"
                className="border-[1.5px] border-[var(--sr-revemoerk)]/25 hover:border-[var(--sr-revemoerk)]/40 focus:border-[var(--sr-revemoerk)] rounded-md px-3.5 py-2.5 text-[14px] bg-white text-[var(--sr-revemoerk)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--sr-reveblaa)]/20 focus:ring-offset-0"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="password" className="text-[12px] font-medium uppercase tracking-[0.04em] opacity-70">
                Passord
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="w-full border-[1.5px] border-[var(--sr-revemoerk)]/25 hover:border-[var(--sr-revemoerk)]/40 focus:border-[var(--sr-revemoerk)] rounded-md px-3.5 py-2.5 pr-11 text-[14px] bg-white text-[var(--sr-revemoerk)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--sr-reveblaa)]/20 focus:ring-offset-0"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Skjul passord' : 'Vis passord'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[var(--sr-revemoerk)]/60 hover:text-[var(--sr-revemoerk)] focus:outline-none focus:ring-2 focus:ring-[var(--sr-reveblaa)]/20 rounded"
                >
                  <SrIcon name={showPassword ? 'eye-off' : 'eye'} size={16} />
                </button>
              </div>
            </div>

            <div className="flex justify-between items-center text-[12.5px] pt-1.5 pb-2">
              <label className="inline-flex items-center gap-2 opacity-80 cursor-pointer">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="rounded border-[var(--sr-revemoerk)]"
                />
                Hold meg pålogget
              </label>
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={resetPassword.isPending}
                className="text-[var(--sr-reveblaa)] font-medium no-underline hover:underline disabled:opacity-60"
              >
                {resetPassword.isPending ? 'Sender…' : 'Glemt passord?'}
              </button>
            </div>

            {error && <p role="alert" className="text-sm text-[var(--sr-red-hex)]">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 border-[1.5px] border-[var(--sr-reveblaa)] bg-[var(--sr-reveblaa)] text-white font-medium text-[14px] px-4 py-3 rounded-md transition-colors hover:bg-[#0715CC] disabled:opacity-60"
            >
              <SrIcon name="arrow-right" size={14} />
              {loading ? 'Logger inn…' : 'Logg inn'}
            </button>
          </form>
          </div>
        </div>
    </div>
  );
}
